import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import BaseChart from "../../baseChart";
import { numFormatter } from "../../../helpers/utils";
import { createDataZoomSlider } from "../../baseChart/utils";
import { formatDateTime } from "./utils";

export type BarLineComboChartConfiguration = {
  config: {
    mark_lines: {
      name: string;
      axis: "y-axis" | "x-axis";
      value: number | string;
      value_formatter: string;
      color?: string;
    }[];
    line: {
      name: string;
      data_point: string;
      selector: string;
      value_formatter: string;
    };
    bar: {
      name: string;
      data_point: string;
      selector: string;
      value_formatter: string;
    };
  };
  data: {
    series: {
      category: string;
      line_value: number;
      bar_value: number;
      line_id: number;
      bar_id: number;
    }[];
  };
};

interface IBarLineComboChartProps {
  width: number;
  height: number;
  data: BarLineComboChartConfiguration;
  extraConfigs?: any;
  limitBars?: number;
}

const BarLineComboChart = ({
  limitBars = 40,
  data,
  width,
  height,
  extraConfigs,
}: IBarLineComboChartProps) => {
  const option = useMemo<EChartsOption>(() => {
    let opt: EChartsOption = {
      tooltip: {},
      legend: {},
      xAxis: [],
      yAxis: [],
      series: [],
    };
    let enableScroll = false;
    if (data) {
      const dataPointCount = data.data.series.length;
      const minWidthPerBar = 30;
      
      enableScroll = dataPointCount > limitBars || (dataPointCount * minWidthPerBar > width);
      
      opt = {
        tooltip: {
          trigger: "axis",
          axisPointer: {
            show: true,
            type: "cross",
          },
        },
        legend: {
          top: "top",
          data: [data.config.bar.name, data.config.line.name],
        },
        grid: {
          left: "0px",
          right: "0px",
          containLabel: true,
          bottom: "30px",
        },
        xAxis: [
          {
            type: "category",
            data: data.data.series.map((d) => d.category),
            axisPointer: {
              show: true,
              type: "shadow",
            },
          },
        ],
        yAxis: [
          {
            type: "value",
            name: data.config.bar.name,
            axisLabel: {
              formatter: (params) => {
                return `${numFormatter(params)} ${
                  data.config.bar.value_formatter
                }`;
              },
            },
            axisPointer: {
              show: true,
              type: "line",
            },
            axisLine: {
              lineStyle: {
                color: "#58585E",
              },
            },
            nameTextStyle: {
              padding: [0, 0, 0, 30],
            },
            splitNumber: 5,
          },
          {
            type: "value",
            name: data.config.line.name,
            axisLabel: {
              formatter: (value) => {
                return `${numFormatter(value)} ${
                  data.config.line.value_formatter
                }`;
              },
            },
            axisLine: {
              lineStyle: {
                color: "#3ECD89",
              },
            },
            nameTextStyle: {
              padding: [0, 30, 0, 0],
            },
            splitNumber: 5,
          },
        ],
        dataZoom: enableScroll ? createDataZoomSlider({ limitBars }) : [],
        series: [
          {
            name: data.config.bar.name,
            type: "bar",
            itemStyle: {
              color: "#58585E",
            },
            tooltip: {
              formatter: "{b0}",
              valueFormatter: function (value) {
                return `${
                  typeof value === "number"
                    ? formatNumber(value)
                    : formatNumber(Number(value))
                } ${data.config.bar.value_formatter}`;
              },
            },
            markLine: data.config.mark_lines
              ? {
                  symbolSize: 0,
                  data: data.config.mark_lines.map((d) => ({
                    name: d.name,
                    [d.axis === "y-axis" ? "yAxis" : "xAxis"]: d.value,
                    lineStyle: {
                      color: d.color ?? "red",
                      width: 3,
                    },
                    label: {
                      show: true,
                      formatter: ({ name, value }) => {
                        return `${name}: ${
                          typeof value === "number"
                            ? formatNumber(value)
                            : formatNumber(Number(value))
                        } ${d.value_formatter}`;
                      },
                      position: "insideMiddle",
                      backgroundColor: d.color ?? "red",
                      color: "#ababab",
                      padding: 3,
                    },
                  })),
                }
              : {},
            data: data.data.series.map((d) => d.bar_value),
          },
          {
            name: data.config.line.name,
            type: "line",
            itemStyle: {
              color: "#3ECD89",
            },
            yAxisIndex: 1,
            tooltip: {
              formatter: "{b1}",
              valueFormatter: function (value) {
                return `${typeof value === "number" ? value : Number(value)} ${
                  data.config.line.value_formatter
                }`;
              },
            },
            data: data.data.series.map((d) => d.line_value),
          },
        ],
      };
      if (extraConfigs) {
        if ("trendPeriod" in extraConfigs) {
          switch (extraConfigs.trendPeriod) {
            case "trends-weekly":
              return {
                ...opt,
                dataZoom: Array.isArray(opt?.dataZoom) 
                  ? opt.dataZoom.map((obj: any) => ({ ...obj, bottom: 0 }))
                  : opt?.dataZoom,
              };
            case "trends-hourly":
              return {
                ...opt,
                xAxis: [
                  {
                    type: "category",
                    data: data.data.series.map((d) => {
                      if ('offset' in d && d.offset) {
                        return formatDateTime(
                          d.category,
                          (d as any).offset,
                          "dd/MM - hh:mm a"
                        );
                      }
                      return d.category;
                    }),
                  },
                ],
              };
            default:
              return opt;
          }
        }
        return opt;
      }
    }
    return opt;
  }, [JSON.stringify(data)]);

  return (
    <BaseChart
      height={height}
      width={width}
      options={option}
      chartType="bar-line"
    />
  );
};

export default BarLineComboChart;
